import type { Incident } from '../../services/incidentsApi';
import {
  representativeClipLikelyOnEdge,
  representativeHasCloudClip,
  representativeHasCloudSnapshot,
} from './groupedEvidenceAutoPromote';
import { isExplicitRepresentativeReviewComplete } from './groupedGuidedWorkflow';

/** Higher = better for opening Evidence review. */
export type GroupedEvidenceRepresentativeScore = 0 | 1 | 2 | 3;

export type GroupedEvidenceRepresentativeDecision = {
  reviewAnchorIncidentId: string;
  reviewAnchorSelectionReason:
    | 'newest_eligible_evidence_complete'
    | 'latest_incident_no_complete_proof_available'
    | 'best_available_proof_in_pattern';
  latestIncidentId: string;
  latestIncidentEvidenceState:
    | 'evidence_complete'
    | 'snapshot_ready_clip_processing'
    | 'clip_ready_snapshot_processing'
    | 'processing'
    | 'unknown';
  anchorReuseAllowed: boolean;
};

export function scoreIncidentForGroupedEvidenceReview(
  incident: Incident | null | undefined,
): GroupedEvidenceRepresentativeScore {
  if (!incident) return 0;
  const hasSnapshot = representativeHasCloudSnapshot(incident);
  const hasClip = representativeHasCloudClip(incident);
  if (hasSnapshot && hasClip) return 3;
  if (hasSnapshot && representativeClipLikelyOnEdge(incident)) return 2;
  if (hasSnapshot) return 1;
  return 0;
}

export function parseIncidentActivityMs(incident: Incident | null | undefined): number {
  const candidates = [
    (incident as any)?.last_edge_update_at,
    (incident as any)?.updated_at,
    (incident as any)?.created_at,
    (incident as any)?.event_timestamp,
  ];
  for (const raw of candidates) {
    const ms = Date.parse(String(raw || '').trim());
    if (Number.isFinite(ms)) return ms;
  }
  return 0;
}

function incidentMatchesPatternKey(
  incident: Incident | null | undefined,
  patternKey: string,
): boolean {
  if (!patternKey) return true;
  return String((incident as any)?.pattern_key || '').trim() === patternKey;
}

function incidentIsEvidenceComplete(incident: Incident | null | undefined): boolean {
  return representativeHasCloudSnapshot(incident) && representativeHasCloudClip(incident);
}

function describeIncidentEvidenceState(
  incident: Incident | null | undefined,
): GroupedEvidenceRepresentativeDecision['latestIncidentEvidenceState'] {
  if (!incident) return 'unknown';
  const hasSnapshot = representativeHasCloudSnapshot(incident);
  const hasClip = representativeHasCloudClip(incident);
  if (hasSnapshot && hasClip) return 'evidence_complete';
  if (hasSnapshot) return 'snapshot_ready_clip_processing';
  if (hasClip) return 'clip_ready_snapshot_processing';
  const snapshotStatus = String((incident as any)?.snapshot_status || '').trim().toUpperCase();
  const clipStatus = String((incident as any)?.clip_status || '').trim().toUpperCase();
  if (snapshotStatus === 'PENDING' || clipStatus === 'PENDING') return 'processing';
  return 'unknown';
}

/**
 * Pick the best incident in a recurring-risk group for Evidence review.
 * Prefers newest row with cloud snapshot+clip, then snapshot+edge clip, then snapshot only.
 */
export function pickGroupedEvidenceRepresentativeIncidentId(
  incidents: Incident[],
  options?: {
    fallbackId?: string;
    preferCloudProof?: boolean;
    patternKey?: string;
    allowReviewedReuse?: boolean;
  },
): string {
  return pickGroupedEvidenceRepresentativeIncident(incidents, options).reviewAnchorIncidentId;
}

export function pickGroupedEvidenceRepresentativeIncident(
  incidents: Incident[],
  options?: {
    fallbackId?: string;
    preferCloudProof?: boolean;
    patternKey?: string;
    allowReviewedReuse?: boolean;
  },
): GroupedEvidenceRepresentativeDecision {
  const patternKey = String(options?.patternKey || '').trim();
  const rows = (incidents || [])
    .filter((row) => String(row?.incident_id || '').trim())
    .filter((row) => incidentMatchesPatternKey(row, patternKey));
  const fallbackId = String(options?.fallbackId || '').trim();
  const latest = [...rows].sort((left, right) => parseIncidentActivityMs(right) - parseIncidentActivityMs(left))[0];
  if (!rows.length) {
    return {
      reviewAnchorIncidentId: fallbackId,
      reviewAnchorSelectionReason: 'latest_incident_no_complete_proof_available',
      latestIncidentId: fallbackId,
      latestIncidentEvidenceState: 'unknown',
      anchorReuseAllowed: Boolean(options?.allowReviewedReuse),
    };
  }

  const evidenceComplete = [...rows]
    .filter((row) => incidentIsEvidenceComplete(row))
    .filter((row) => options?.allowReviewedReuse || !isExplicitRepresentativeReviewComplete(row as any))
    .sort((left, right) => parseIncidentActivityMs(right) - parseIncidentActivityMs(left));

  if (evidenceComplete[0]?.incident_id) {
    return {
      reviewAnchorIncidentId: String(evidenceComplete[0].incident_id).trim(),
      reviewAnchorSelectionReason: 'newest_eligible_evidence_complete',
      latestIncidentId: String(latest?.incident_id || fallbackId).trim() || fallbackId,
      latestIncidentEvidenceState: describeIncidentEvidenceState(latest),
      anchorReuseAllowed: Boolean(options?.allowReviewedReuse),
    };
  }

  const ranked = [...rows].sort((left, right) => {
    const scoreDiff =
      scoreIncidentForGroupedEvidenceReview(right) - scoreIncidentForGroupedEvidenceReview(left);
    if (scoreDiff !== 0) return scoreDiff;
    return parseIncidentActivityMs(right) - parseIncidentActivityMs(left);
  });

  if (options?.preferCloudProof) {
    const cloudReady = ranked.find((row) => scoreIncidentForGroupedEvidenceReview(row) === 3);
    if (cloudReady?.incident_id) {
      return {
        reviewAnchorIncidentId: String(cloudReady.incident_id).trim(),
        reviewAnchorSelectionReason: 'best_available_proof_in_pattern',
        latestIncidentId: String(latest?.incident_id || fallbackId).trim() || fallbackId,
        latestIncidentEvidenceState: describeIncidentEvidenceState(latest),
        anchorReuseAllowed: Boolean(options?.allowReviewedReuse),
      };
    }
  }

  return {
    reviewAnchorIncidentId: String(ranked[0]?.incident_id || fallbackId).trim() || fallbackId,
    reviewAnchorSelectionReason: 'latest_incident_no_complete_proof_available',
    latestIncidentId: String(latest?.incident_id || fallbackId).trim() || fallbackId,
    latestIncidentEvidenceState: describeIncidentEvidenceState(latest),
    anchorReuseAllowed: Boolean(options?.allowReviewedReuse),
  };
}

export function groupedEvidenceReviewHasCloudProof(
  incident: Incident | null | undefined,
): boolean {
  return scoreIncidentForGroupedEvidenceReview(incident) === 3;
}
